import { useCallback, useSyncExternalStore } from "react";
import { useAudioPlayer } from "./hook.js";

export interface UseAudioPlayerTriggerProps {
  /** The source this fragment belongs to. */
  sourceKey: string;
  /** The start time of this fragment. Defaults to 0. */
  time?: number;
  /** The start time of the next fragment. Used to determine whether this fragment is playing. Defaults to Infinity. */
  nextTime?: number | undefined;
}

export interface AudioPlayerTriggerState {
  /** Whether the playhead is within this fragment, including while paused. */
  isCurrent: boolean;
  /** Whether this fragment is playing. */
  isPlaying: boolean;
  /** Plays the source from the start of this fragment. */
  play: () => void;
  pause: () => void;
}

export function useAudioPlayerTrigger(
  props: UseAudioPlayerTriggerProps,
): AudioPlayerTriggerState {
  const { sourceKey, time = 0, nextTime = Infinity } = props;

  const audioPlayer = useAudioPlayer();
  const nativeAudio = audioPlayer.nativeAudio;
  const isPlayingSource = audioPlayer.currentSourceKey === sourceKey;

  // Do this all in the same hook to avoid unnecessary re-renders, since this will be used for many fragments on the page
  const fragmentState = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        // We don't need to subscribe/unsubscribe if we're not playing our audio source
        if (!nativeAudio || !isPlayingSource) {
          return () => {};
        }

        nativeAudio.addEventListener("emptied", onStoreChange);
        nativeAudio.addEventListener("play", onStoreChange);
        nativeAudio.addEventListener("pause", onStoreChange);
        nativeAudio.addEventListener("seeking", onStoreChange);
        nativeAudio.addEventListener("timeupdate", onStoreChange);

        return () => {
          nativeAudio.removeEventListener("emptied", onStoreChange);
          nativeAudio.removeEventListener("play", onStoreChange);
          nativeAudio.removeEventListener("pause", onStoreChange);
          nativeAudio.removeEventListener("seeking", onStoreChange);
          nativeAudio.removeEventListener("timeupdate", onStoreChange);
        };
      },
      [nativeAudio, isPlayingSource],
    ),
    (): "inactive" | "paused" | "playing" => {
      if (!nativeAudio || !isPlayingSource) {
        return "inactive";
      }

      const isInTimeRange =
        nativeAudio.currentTime >= time && nativeAudio.currentTime < nextTime;

      if (!isInTimeRange) {
        return "inactive";
      }
      if (!nativeAudio.paused) {
        return "playing";
      }
      // A freshly loaded source rests at 0 before the user has played or
      // sought anything, so the fragment at 0 isn't current yet.
      const hasPlayhead =
        nativeAudio.currentTime > 0 || nativeAudio.played.length > 0;
      return hasPlayhead ? "paused" : "inactive";
    },
  );

  const { play: playSource } = audioPlayer;
  const play = useCallback(
    () => playSource(sourceKey, time),
    [playSource, sourceKey, time],
  );
  const pause = useCallback(() => nativeAudio?.pause(), [nativeAudio]);

  return {
    isCurrent: fragmentState !== "inactive",
    isPlaying: fragmentState === "playing",
    play,
    pause,
  };
}
